import React, { useMemo, useState } from "react";
import { X, Search } from "lucide-react";
import { WIZARD_TEMPLATES, PARTY_LAYER_COLOR } from "../../data/wizardTemplates";
import { useTenants } from "../../api/sitroom";
import { SectionLabel, inputBase } from "./wizardAtoms";

export const WizardStep3Parties = ({ form, setForm }) => {
  const tpl = WIZARD_TEMPLATES.find((t) => t.value === form.template);
  const autoLinked = tpl?.parties || [];
  const [search, setSearch] = useState("");
  const { data: tenants = [] } = useTenants();

  const matches = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    return tenants
      .filter(
        (t) =>
          t.name?.toLowerCase().includes(q) ||
          (t.type || "").toLowerCase().includes(q) ||
          (t.layer || "").toLowerCase().includes(q)
      )
      .filter((t) => !(form.additionalParties || []).some((a) => a.id === t.id))
      .slice(0, 8);
  }, [search, tenants, form.additionalParties]);

  const addParty = (t) => {
    setForm((f) => ({
      ...f,
      additionalParties: [...(f.additionalParties || []), { ...t, role: "Responder" }],
    }));
    setSearch("");
  };
  const removeParty = (id) =>
    setForm((f) => ({
      ...f,
      additionalParties: (f.additionalParties || []).filter((p) => p.id !== id),
    }));
  const setRole = (id, role) =>
    setForm((f) => ({
      ...f,
      additionalParties: (f.additionalParties || []).map((p) => (p.id === id ? { ...p, role } : p)),
    }));

  const emails = autoLinked.map((p) => `${p.toLowerCase().replace(/\s+/g, ".")}@${p.includes("OEM") ? "bmw.de" : "supplier.com"}`);
  form.additionalParties?.forEach((p) => emails.push(`${p.name.toLowerCase().replace(/[^a-z0-9]+/g, ".")}@${p.layer === "OEM" ? "bmw.de" : "supplier.com"}`));

  return (
    <div data-testid="wizard-step3">
      <h2
        style={{
          fontFamily: "Syne, sans-serif",
          fontSize: 22,
          fontWeight: 600,
          color: "#f0f2f8",
          marginBottom: 4,
          letterSpacing: "-0.3px",
        }}
      >
        Who needs to be in the room?
      </h2>
      <p
        style={{
          fontFamily: "'DM Mono', monospace",
          fontSize: 11,
          color: "#9198b0",
          letterSpacing: "0.5px",
          marginBottom: 24,
        }}
      >
        Auto-linked from your template — add observers or domain specialists below.
      </p>

      <SectionLabel>Auto-linked parties · from template</SectionLabel>
      <div className="grid gap-2 mb-6" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
        {autoLinked.map((p) => (
          <div
            key={p}
            className="flex items-center gap-2 px-3 py-2 rounded"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.08)",
              opacity: 0.95,
            }}
            data-testid={`wizard-auto-party-${p}`}
          >
            <span
              className="inline-block rounded-full"
              style={{ width: 7, height: 7, background: PARTY_LAYER_COLOR[p] || "#9198b0" }}
            />
            <span
              style={{
                flex: 1,
                fontFamily: "Syne, sans-serif",
                fontWeight: 600,
                fontSize: 12,
                color: "#f0f2f8",
              }}
            >
              {p}
            </span>
            <span
              title="Required by template"
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 9,
                color: "#6b7388",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Required
            </span>
          </div>
        ))}
      </div>

      <SectionLabel>Add additional parties</SectionLabel>
      <div className="relative" style={{ maxWidth: 480 }}>
        <Search
          size={13}
          style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#6b7388" }}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tenants by name or type…"
          style={{ ...inputBase, paddingLeft: 28 }}
          data-testid="wizard-party-search"
        />
        {matches.length > 0 && (
          <div
            className="absolute left-0 right-0 mt-1 rounded overflow-hidden z-10"
            style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.15)" }}
          >
            {matches.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => addParty(t)}
                className="w-full text-left px-3 py-1.5 transition-colors flex items-center gap-2"
                style={{ background: "transparent", border: "none", cursor: "pointer" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(24,95,165,0.10)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                data-testid={`wizard-tenant-match-${t.id}`}
              >
                <span
                  style={{
                    fontFamily: "Syne, sans-serif",
                    fontWeight: 600,
                    fontSize: 12,
                    color: "#f0f2f8",
                    flex: 1,
                  }}
                >
                  {t.name}
                </span>
                <span
                  className="px-1.5 py-0.5 rounded"
                  style={{
                    background: "rgba(94,163,227,0.10)",
                    border: "1px solid rgba(94,163,227,0.30)",
                    color: "#9ec3ec",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                  }}
                >
                  {t.type || t.layer}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {(form.additionalParties || []).length > 0 && (
        <div className="grid gap-2 mt-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
          {form.additionalParties.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-2 px-3 py-2 rounded"
              style={{
                background: "rgba(24,95,165,0.06)",
                border: "1px solid rgba(24,95,165,0.30)",
              }}
              data-testid={`wizard-additional-party-${p.id}`}
            >
              <span
                className="inline-block rounded-full"
                style={{ width: 7, height: 7, background: PARTY_LAYER_COLOR[p.type] || "#9ec3ec" }}
              />
              <span style={{ flex: 1, fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "#f0f2f8" }}>
                {p.name}
              </span>
              <select
                value={p.role}
                onChange={(e) => setRole(p.id, e.target.value)}
                style={{
                  background: "#0d0f18",
                  border: "1px solid rgba(255,255,255,0.10)",
                  color: "#c5cad8",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 10,
                  padding: "3px 6px",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                <option value="Responder">Responder</option>
                <option value="Observer">Observer</option>
              </select>
              <button
                type="button"
                onClick={() => removeParty(p.id)}
                style={{ background: "transparent", border: "none", color: "#E24B4A", cursor: "pointer", padding: 0 }}
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div
        className="mt-6 rounded-md p-4"
        style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.08)" }}
        data-testid="wizard-notification-preview"
      >
        <SectionLabel>Notification preview</SectionLabel>
        <div
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
            color: "#c5cad8",
            lineHeight: 1.7,
          }}
        >
          <div>
            <span style={{ color: "#9ec3ec" }}>Email</span> → {emails.slice(0, 4).join(", ")}
            {emails.length > 4 && <span style={{ color: "#6b7388" }}> +{emails.length - 4} more</span>}
          </div>
          <div>
            <span style={{ color: "#9ec3ec" }}>SMS</span> →{" "}
            {form.severity === "s1" ? "All responders (S1 escalation)" : "Responders only"}
          </div>
          <div>
            <span style={{ color: "#9ec3ec" }}>In-app</span> → All linked-party users will receive an in-app notification
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <label
          className="flex items-center gap-2 cursor-pointer"
          style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c5cad8" }}
        >
          <input
            type="checkbox"
            checked={form.notifyNow}
            onChange={(e) => setForm((f) => ({ ...f, notifyNow: e.target.checked }))}
            data-testid="wizard-notify-now"
          />
          Notify immediately on submit
        </label>
      </div>
    </div>
  );
};
